import { Box, IconButton, Textarea, VStack, useToast } from "@chakra-ui/react"
import { pick } from "@pathwright/ui/src/components/filestack/utils"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { getImage } from "@pathwright/ux/src/components/avatar/utils"
import DiscardButton from "@pathwright/ux/src/components/form/DiscardButton"
import Field from "@pathwright/ux/src/components/form/Field"
import SubmitButton from "@pathwright/ux/src/components/form/SubmitButton"
import {
  FieldTip,
  TippedField
} from "@pathwright/ux/src/components/form/TippedField"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { Form, FormikProps } from "formik"
import { motion } from "framer-motion"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import { useUpdateUserMutation } from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

const tPrefix = "profile.form"

const validationSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Invalid email address"),
  bio: z.string(),
  location: z.string(),
  avatar: z.string()
})

type Values = z.infer<typeof validationSchema>

const formikValidator = toFormikValidate(validationSchema)

function UserAugmentationSettings() {
  const { t } = useTranslate()
  const me = usePathwrightContext().me!
  const [updateUser] = useUpdateUserMutation()

  const initialValues: Values = {
    firstName: me.first_name,
    lastName: me.last_name,
    email: me.email || "",
    bio: me.profile?.bio || "",
    location: me.profile?.location || "",
    avatar: me.profile?.imageRaw || ""
  }

  async function handleSubmit(values: Values) {
    await updateUser({
      variables: {
        user: {
          id: me.id!,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email
        },
        profile: {
          id: me.profile?.id!,
          bio: values.bio,
          location: values.location,
          avatar: values.avatar
        }
      }
    })
  }

  const toast = useToast()

  const handlePickAvatar = (form: FormikProps<Values>) => async () => {
    const result = await pick({
      // NOTE: derived these options from legacy ui "image" media type options.
      imageMax: [2000, 2000],
      maxFiles: 1,
      uploadInBackground: false,
      fromSources: [
        "local_file_system",
        "dropbox",
        "googledrive",
        "onedrive",
        "facebook",
        "unsplash",
        "imagesearch",
        "webcam",
        "instagram"
      ],
      maxSize: 100 * 1024 * 1024,
      accept: ["image/*", ".heic"]
    })

    const { filesUploaded, filesFailed } = result
    const url = filesUploaded?.[0]?.url
    // Successfully uploaded a file, set in form state.
    if (url) {
      form.setFieldValue("avatar", url)
    } else if (filesFailed?.length) {
      // Some error occurred, let the user know.
      toast({
        title: `Error uploading file: ${filesFailed[0].filename}`,
        status: "error",
        duration: 3000,
        isClosable: true,
        icon: <Pathicon icon="caution-triangle" />
      })
    }
  }

  const visbleToAllTip: FieldTip = {
    icon: "eye",
    title: t(`${tPrefix}.visibility_tip_all.Visible to all members.`)
  }

  const visibleToStaffTip: FieldTip = {
    icon: "eye-off",
    title: t(`${tPrefix}.visibility_tip_staff.Visible to you and staff only.`)
  }

  return (
    <FormikWithDiscardFormState
      initialValues={initialValues}
      validate={formikValidator}
      onSubmit={handleSubmit}
      enableReinitialize
      // TODO: it seems the form state is reset on submit (just a "visiual" glitch
      // as the changes are properly saved), but we should fix this.
      preserveFormStateOnSubmit={true}
    >
      {/* TODO: need to fix typing in FormikWithDiscardFormState to provide
      proper form typing by default. */}
      {(form: FormikProps<Values>) => (
        <Box
          as={Form}
          className="UserAugmentationSettings"
          w="100%"
          sx={{
            ".chakra-input__right-addon .TooltipWrapper": {
              opacity: 0.6
            },
            // We should find a way to apply this to chakra's theme without affecting all inputs.
            input: {
              _focusVisible: {
                bg: "gray.100"
              }
            }
          }}
        >
          <KeyboardFormSubmit />
          <VStack spacing={4} p={4} width="100%">
            <Box pos="relative">
              <UserAvatar
                user={{
                  ...me,
                  profile: {
                    ...me.profile,
                    image: form.values.avatar
                  }
                }}
                preferIcon
                bg={getImage(me) ? undefined : "gray.200"}
                boxSize="4em"
                borderRadius="full"
                onClick={handlePickAvatar(form)}
                cursor="pointer"
              />
              <IconButton
                aria-label="Change avatar"
                // variant="ghost"
                icon={<Pathicon icon={getImage(me) ? "shuffle" : "upload"} />}
                onClick={handlePickAvatar(form)}
                h={8}
                minW={8}
                isRound
                pos="absolute"
                top={0}
                right={0}
                transform="translate(25%,-25%);"
                cursor="pointer"
              />
            </Box>
            <TippedField
              name="firstName"
              label={t(`${tPrefix}.first_name`)}
              placeholder={t(`${tPrefix}.first_name_ph`)}
              tip={visbleToAllTip}
            />
            <TippedField
              name="lastName"
              label={t(`${tPrefix}.last_name`)}
              placeholder={t(`${tPrefix}.last_name_ph`)}
              tip={visibleToStaffTip}
            />
            <TippedField
              name="email"
              label={t(`${tPrefix}.email`)}
              placeholder={t(`${tPrefix}.email_ph`)}
              tip={visibleToStaffTip}
            />
            <TippedField
              name="location"
              label={t(`${tPrefix}.location`)}
              placeholder={t(`${tPrefix}.location_ph`)}
              tip={visbleToAllTip}
            />
            <Field
              as={Textarea}
              name="bio"
              label={t(`${tPrefix}.about_you`)}
              placeholder={t(`${tPrefix}.about_you_ph`)}
              borderRadius="20px"
              // Padding for visibility icon.
              pr="calc(2 *var(--input-padding))"
              // TODO: mv to chakra theme?
              _focusVisible={{
                bg: "white"
              }}
            >
              {({ field }) => (
                <Box position="relative">
                  {field}
                  <Tooltip title={visbleToAllTip.title} fitContent>
                    <Pathicon
                      icon={visbleToAllTip.icon}
                      size="1em"
                      pos="absolute"
                      // Top/right to match sizing of input padding used in InputGroup.
                      // (InputGroup doesn't seem to work with Textarea, and wouldn't expect it to.)
                      top="1rem"
                      right="1rem"
                      transform="translateY(-50%)"
                      opacity=".6"
                    />
                  </Tooltip>
                </Box>
              )}
            </Field>
            {form.dirty && (
              <VStack
                spacing={4}
                width="100%"
                as={motion.div}
                initial={{ opacity: 0, y: -30 }}
                animate={{
                  opacity: 1,
                  y: 0
                }}
                w="100%"
              >
                <SubmitButton>{t(`${tPrefix}.save`)}</SubmitButton>
                <DiscardButton colorScheme="gray">
                  {t(`${tPrefix}.discard`)}
                </DiscardButton>
              </VStack>
            )}
          </VStack>
        </Box>
      )}
    </FormikWithDiscardFormState>
  )
}

export default UserAugmentationSettings
